// src/pages/tenant/TenantMembersPage.tsx
// Member management: list, invite, remove, change roles, transfer ownership.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLES = ['owner', 'admin', 'manager', 'member', 'viewer'] as const;
type Role = (typeof ROLES)[number];

const ROLE_COLORS: Record<Role, string> = {
  owner: 'purple',
  admin: 'red',
  manager: 'warning',
  member: 'info',
  viewer: 'secondary',
};

const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
  viewer: 'Viewer',
};

const STATUS_VARIANTS: Record<string, string> = {
  active: 'success',
  invited: 'warning',
  suspended: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract initials from a display name. */
function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Deterministic color for an avatar based on name hash. */
function avatarColor(name: string): string {
  const colors = [
    '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
    '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#06b6d4',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

/** Format a date string as relative time (e.g. "3 days ago"). */
function relativeTime(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    const date = new Date(dateStr).getTime();
    const now = Date.now();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 5) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return `${diffYears}y ago`;
  } catch {
    return String(dateStr);
  }
}

/** Normalize a role string to a known Role key. */
function normalizeRole(role: string | undefined): Role {
  if (!role) return 'member';
  const lower = role.toLowerCase().replace(/^role_/, '');
  if (ROLES.includes(lower as Role)) return lower as Role;
  return 'member';
}

/** Normalize a status string to a known status key. */
function normalizeStatus(status: string | undefined): string {
  if (!status) return 'active';
  const lower = status.toLowerCase().replace(/^member_status_/, '');
  if (lower in STATUS_LABELS) return lower;
  return 'active';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantMembersPage - member management for a tenant.
 * Route: /tenants/:id/members
 */
export const TenantMembersPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  // Invite form state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('member');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Role change state
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<Role>('member');

  // Remove confirm state
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Transfer ownership state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferConfirmText, setTransferConfirmText] = useState('');
  const [transferring, setTransferring] = useState(false);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const [members, setMembers] = useState<any[]>([]);

  const loadMembers = useCallback(() => {
    if (!id) return;
    store.ListTenantMembers(id).then((res: any) => {
      const items = res?.members?.members ?? res?.members?.items;
      setMembers(Array.isArray(items) ? items : []);
    }).catch(() => {});
  }, [id, searchParams]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      await store.AddTenantMember(id, {
        user: { id: inviteEmail.trim() } as any,
        role: `TENANT_ROLE_${inviteRole.toUpperCase()}`,
      });
      setInviteEmail('');
      setInviteRole('member');
      setInviteMessage('');
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
      loadMembers();
    } catch {
      // Error handled by store
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async (memberId: string) => {
    if (!id) return;
    try {
      await store.UpdateTenantMemberRole(id, memberId, {
        role: `TENANT_ROLE_${newRole.toUpperCase()}`,
      });
      setChangingRoleId(null);
      loadMembers();
    } catch {
      // Error handled by store
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!id) return;
    try {
      await store.RemoveTenantMember(id, memberId);
      setRemovingId(null);
      loadMembers();
    } catch {
      // Error handled by store
    }
  };

  const handleTransferOwnership = async () => {
    if (!id || !transferTargetId) return;
    setTransferring(true);
    try {
      await store.TransferTenantOwnership(id, {
        new_owner: { id: transferTargetId } as any,
      });
      setShowTransfer(false);
      setTransferTargetId('');
      setTransferConfirmText('');
      loadMembers();
    } catch {
      // Error handled by store
    } finally {
      setTransferring(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const nonOwnerMembers = members.filter(
    (m: any) => normalizeRole(m.role) !== 'owner',
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
          {t('tenant.settings')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('tenant.members')}</span>
      </nav>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('tenant.members')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('tenant.membersDescription')}
          </p>
        </div>
        <Button onClick={() => setShowInvite(!showInvite)}>
          {showInvite ? t('common.cancel') : t('tenant.inviteMember')}
        </Button>
      </div>

      {/* Notifications */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {inviteSuccess && (
        <Alert variant="success" dismissible onDismiss={() => setInviteSuccess(false)}>
          {t('tenant.inviteSuccess')}
        </Alert>
      )}

      {/* Invite form (collapsible) */}
      {showInvite && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tenant.inviteMember')}</CardTitle>
            <CardDescription>{t('tenant.inviteDescription')}</CardDescription>
          </CardHeader>
          <form onSubmit={handleInvite}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t('tenant.email')}
                  </label>
                  <Input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    fullWidth={false}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t('tenant.role')}
                  </label>
                  <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)} fullWidth={false}>
                    {ROLES.filter((r) => r !== 'owner').map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('tenant.inviteMessageLabel')}
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder={t('tenant.inviteMessagePlaceholder')}
                  rows={3}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowInvite(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
                {inviting ? t('common.loading') : t('tenant.sendInvite')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* Members table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{/* Avatar */}</TableHead>
              <TableHead>{t('tenant.memberName')}</TableHead>
              <TableHead>{t('tenant.role')}</TableHead>
              <TableHead>{t('tenant.status')}</TableHead>
              <TableHead>{t('tenant.joined')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableEmpty colSpan={6} message={t('tenant.noMembers')} />
            ) : (
              members.map((member: any) => {
                const memberId = member.member?.id || member.entity?.id || member.id;
                const memberName = member.name || member.display_name || member.email || '';
                const memberEmail = member.email || '';
                const role = normalizeRole(member.role);
                const status = normalizeStatus(member.status);
                const isChangingRole = changingRoleId === memberId;
                const isRemoving = removingId === memberId;

                return (
                  <TableRow key={memberId || Math.random()}>
                    {/* Avatar */}
                    <TableCell>
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                        style={{ backgroundColor: avatarColor(memberName) }}
                      >
                        {getInitials(memberName)}
                      </div>
                    </TableCell>

                    {/* Name + email */}
                    <TableCell>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {memberName || '--'}
                        </div>
                        {memberEmail && (
                          <div className="text-xs text-muted-foreground">{memberEmail}</div>
                        )}
                      </div>
                    </TableCell>

                    {/* Role */}
                    <TableCell>
                      {isChangingRole ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value as Role)} fullWidth={false}>
                            {ROLES.filter((r) => r !== 'owner').map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABELS[r]}
                              </option>
                            ))}
                          </Select>
                          <Button
                            size="sm"
                            onClick={() => handleChangeRole(memberId)}
                          >
                            {t('common.save')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setChangingRoleId(null)}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      ) : (
                        <Badge variant={ROLE_COLORS[role] as any} size="sm">
                          {ROLE_LABELS[role]}
                        </Badge>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[status] as any} size="sm">
                        {STATUS_LABELS[status] || status}
                      </Badge>
                    </TableCell>

                    {/* Joined */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {relativeTime(member.joined_at || member.created_at)}
                      </span>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      {isRemoving ? (
                        <div className="flex justify-end items-center gap-2">
                          <span className="text-xs text-destructive">
                            {t('tenant.confirmRemove')}
                          </span>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleRemove(memberId)}
                          >
                            {t('common.confirm')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRemovingId(null)}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          {role !== 'owner' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setChangingRoleId(memberId);
                                  setNewRole(role);
                                }}
                              >
                                {t('tenant.changeRole')}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => setRemovingId(memberId)}
                              >
                                {t('tenant.remove')}
                              </Button>
                            </>
                          )}
                          {role === 'owner' && (
                            <span className="text-xs text-muted-foreground px-2 py-1">
                              {t('tenant.ownerProtected')}
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          {members.length} {t('tenant.membersCount')}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!searchParams.get('cursor')}
            onClick={() => {
              const params = Object.fromEntries(searchParams.entries());
              delete params.cursor;
              setSearchParams(params);
            }}
          >
            {t('common.first')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={members.length < 20}
            onClick={() => {
              const lastItem = members[members.length - 1];
              const cursor = lastItem?.entity?.id || lastItem?.id || '';
              if (cursor) {
                const params = Object.fromEntries(searchParams.entries());
                params.cursor = cursor;
                setSearchParams(params);
              }
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>

      {/* Transfer Ownership (danger zone) */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">{t('tenant.dangerZone')}</CardTitle>
          <CardDescription>{t('tenant.dangerZoneDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!showTransfer ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t('tenant.transferOwnership')}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('tenant.transferOwnershipDescription')}
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowTransfer(true)}
              >
                {t('tenant.transferOwnership')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert variant="warning">
                {t('tenant.transferWarning')}
              </Alert>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('tenant.selectNewOwner')}
                </label>
                <Select
                  value={transferTargetId}
                  onChange={(e) => setTransferTargetId(e.target.value)} fullWidth={false}>
                  <option value="">{t('tenant.selectMember')}</option>
                  {nonOwnerMembers.map((m: any) => {
                    const mId = m.entity?.id || m.id;
                    const mName = m.name || m.display_name || m.email || mId;
                    return (
                      <option key={mId} value={mId}>
                        {mName} ({ROLE_LABELS[normalizeRole(m.role)]})
                      </option>
                    );
                  })}
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('tenant.transferConfirmLabel')}
                </label>
                <Input
                  placeholder="TRANSFER"
                  value={transferConfirmText}
                  onChange={(e) => setTransferConfirmText(e.target.value)}
                  fullWidth={false}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  {t('tenant.transferConfirmHint')}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={
                    transferring ||
                    !transferTargetId ||
                    transferConfirmText !== 'TRANSFER'
                  }
                  onClick={handleTransferOwnership}
                >
                  {transferring
                    ? t('common.loading')
                    : t('tenant.confirmTransfer')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowTransfer(false);
                    setTransferTargetId('');
                    setTransferConfirmText('');
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default TenantMembersPage;
